import { useCallback } from 'react';

import { Trash2Icon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Tooltip } from '@/_components';
import { useBoolean } from '@/_shared';

import type { DeleteButtonProps } from './type';

import { ActionButton } from '../actions';

export function DeleteButton({
  callback,
  isDeleting = false,
  size = 'md',
  type = 'default',
  title = 'Delete Record',
  text = 'Deleting the record is permanent, and can not be reversed. Please make sure you want to delete the record before continuing.',
  yesButton = 'Yes, Delete',
  noButton = 'No Sorry',
  confirmDelete = false,
  className,
  tooltipTitle = 'Delete Record',
  tooltipPlacement = 'bottom',
  ...props
}: DeleteButtonProps) {
  const [isOpen, open] = useBoolean();

  const handleConfirm = useCallback(async () => {
    try {
      await callback();
      open.setFalse();
    } catch (error) {
      console.error(error);
    }
  }, [callback, open]);

  const ButtonComponent =
    type === 'default' ? (
      <Button
        as="outline"
        theme="error"
        tag="div"
        size={size}
        iconClassName="xs:block hidden"
        iconLeft={Trash2Icon}
        onClick={!confirmDelete ? callback : open.setTrue}
        className={className}
        showLoader={isDeleting}
      >
        Delete
      </Button>
    ) : (
      <ActionButton
        size={size}
        tag="div"
        as="link"
        className={twMerge('shrink-0 opacity-40 hover:opacity-100', className)}
        icon={Trash2Icon}
        onClick={!confirmDelete ? callback : open.setTrue}
        showLoader={isDeleting}
        title={tooltipTitle}
        {...props}
      />
    );

  return (
    <>
      <Modal open={isOpen} onClose={open.setFalse} hideCloseButton disableInternalClosing={isDeleting}>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>{text}</ModalBody>
        <ModalFooter>
          <Button disabled={isDeleting} tag="div" modal onClick={open.setFalse}>
            {noButton}
          </Button>
          <Button showLoader={isDeleting} tag="div" as="outline" modal onClick={handleConfirm}>
            {yesButton}
          </Button>
        </ModalFooter>
      </Modal>
      {tooltipTitle && type === 'default' ? (
        <Tooltip content={tooltipTitle} className="z-999999999" placement={tooltipPlacement}>
          {ButtonComponent}
        </Tooltip>
      ) : (
        ButtonComponent
      )}
    </>
  );
}
